import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { api } from '../api';
import type { ResponseItem, TeamMember } from '../types';

type Props = { onClose: () => void; currentUser: string; items: ResponseItem[]; adminKey?: string; };

export function AdminPanel({ onClose, currentUser, items, adminKey }: Props) {
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState('member');
  const [copied, setCopied] = useState(false);

  const load = () => { api.team.list().then(setTeam); };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newName.trim()) return;
    await api.team.add(newName, newRole, false);
    setNewName('');
    load();
  };

  const remove = async (id: string) => {
    if (!confirm('Deactivate this team member?')) return;
    await api.team.remove(id);
    load();
  };

  const copyClaudePrompt = async () => {
    const lines: string[] = [];
    lines.push(`# Coastline K9 — Weekly Retro Analysis Request`);
    lines.push(`Week: ${items[0]?.weekKey ?? '(current)'}`);
    lines.push(`Requested by: ${currentUser}`);
    lines.push('');
    lines.push(`## Team Responses`);
    for (const r of items) {
      lines.push(`### ${r.memberName}`);
      if (r.perfect)  lines.push(`- PERFECT: ${r.perfect}`);
      if (r.wrong)    lines.push(`- WRONG: ${r.wrong}`);
      if (r.timeSink) lines.push(`- TIME SINK: ${r.timeSink}`);
      if (r.ask)      lines.push(`- ASK ERICK: ${r.ask}`);
      lines.push('');
    }
    lines.push(`## What I need from you, Claude`);
    lines.push(`1. Top 3 themes across the team`);
    lines.push(`2. Systemic issues (things multiple people flagged)`);
    lines.push(`3. Which fixes should ship this week vs. defer`);
    lines.push(`4. Draft 2-3 sentence acknowledgement Erick can send to the team`);
    const text = lines.join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2400);
    } catch {
      alert('Clipboard unavailable — copy manually:\n\n' + text);
    }
  };

  return (
    <>
      <motion.div
        className="sheet-backdrop"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      />
      <motion.aside
        className="sheet"
        initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 32, stiffness: 320 }}
        role="dialog" aria-label="Admin Panel"
      >
        <div className="sheet-handle" />
        <header className="px-4 py-2 border-b border-coast-hairline flex items-center justify-between">
          <h2 className="font-heading text-2xl">Admin</h2>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-8">
          <section>
            <h3 className="font-heading text-xl mb-2">Team Roster</h3>
            <div className="space-y-2 mb-3">
              {team.map(m => (
                <div key={m.id} className="flex items-center justify-between border border-coast-hairline px-3 py-3">
                  <div className="min-w-0">
                    <div className="text-base leading-tight">
                      {m.name} {m.isAdmin ? <span className="text-coast-blue text-xs ml-1">admin</span> : null}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-coast-muted">
                      {m.role.replace('_', ' ')}
                    </div>
                  </div>
                  {!m.isAdmin && (
                    <button className="chip active:border-semantic-red" onClick={() => remove(m.id)}>
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <input
                className="input"
                placeholder="New member name"
                value={newName}
                onChange={e => setNewName(e.target.value)}
              />
              <select
                className="select"
                value={newRole}
                onChange={e => setNewRole(e.target.value)}
              >
                <option value="member">member</option>
                <option value="trainer">trainer</option>
                <option value="admin_asst">admin assistant</option>
                <option value="kennel">kennel</option>
              </select>
              <button className="btn btn-primary btn-block" onClick={add}>Add Member</button>
            </div>
          </section>

          <section>
            <h3 className="font-heading text-xl mb-2">Export</h3>
            <a className="btn btn-block" href={api.exportCsvUrl(adminKey)} download>
              ⬇ Export All Responses (CSV)
            </a>
          </section>

          <section>
            <h3 className="font-heading text-xl mb-2">Analyze with Claude</h3>
            <p className="text-xs text-coast-muted mb-2">
              Copies a formatted prompt of this week&apos;s responses to your clipboard.
              Paste into Claude for themes, patterns, and a draft reply.
            </p>
            <button className="btn btn-primary btn-block" onClick={copyClaudePrompt}>
              {copied ? '✓ Copied — paste into Claude' : 'Copy Retro to Clipboard'}
            </button>
          </section>
        </div>
      </motion.aside>
    </>
  );
}
